
import { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

type TimePeriod = 'today' | 'yesterday' | 'week' | 'month';
type TrendPeriod = '7days' | '30days' | '90days' | 'custom';

const userGrowthData = [
  { date: '01-15', new: 245, total: 12450, active: 8920 },
  { date: '01-16', new: 312, total: 12762, active: 9150 },
  { date: '01-17', new: 289, total: 13051, active: 8890 },
  { date: '01-18', new: 356, total: 13407, active: 9340 },
  { date: '01-19', new: 423, total: 13830, active: 9680 },
  { date: '01-20', new: 398, total: 14228, active: 9520 },
  { date: '01-21', new: 445, total: 14673, active: 10120 },
];

const revenueData = [
  { date: '01-15', consumption: 45600, recharge: 52300 },
  { date: '01-16', consumption: 48900, recharge: 49800 },
  { date: '01-17', consumption: 43200, recharge: 55600 },
  { date: '01-18', consumption: 51200, recharge: 58900 },
  { date: '01-19', consumption: 49800, recharge: 61200 },
  { date: '01-20', consumption: 47600, recharge: 54800 },
  { date: '01-21', consumption: 53400, recharge: 63500 },
];

const instanceData = [
  { date: '01-15', created: 156, deleted: 89, net: 67 },
  { date: '01-16', created: 178, deleted: 92, net: 86 },
  { date: '01-17', created: 145, deleted: 78, net: 67 },
  { date: '01-18', created: 198, deleted: 85, net: 113 },
  { date: '01-19', created: 212, deleted: 95, net: 117 },
  { date: '01-20', created: 189, deleted: 88, net: 101 },
  { date: '01-21', created: 223, deleted: 102, net: 121 },
];

export default function Overview() {
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('today');
  const [trendPeriod, setTrendPeriod] = useState<TrendPeriod>('7days');

  const metrics = [
    {
      title: '累计用户数',
      value: '14,673',
      change: '+8.5%',
      trend: 'up',
      icon: 'ri-user-line',
      color: 'teal',
    },
    {
      title: '活跃用户数（日活）',
      value: '10,120',
      change: '+6.3%',
      trend: 'up',
      icon: 'ri-user-star-line',
      color: 'blue',
    },
    {
      title: '在线实例数',
      value: '3,456',
      change: '峰值: 4,123',
      trend: 'neutral',
      icon: 'ri-computer-line',
      color: 'purple',
    },
    {
      title: '累计充值',
      value: '¥2,456,789',
      change: '+12.8%',
      trend: 'up',
      icon: 'ri-money-cny-circle-line',
      color: 'green',
    },
    {
      title: '新增用户数',
      value: '445',
      change: '+11.8%',
      trend: 'up',
      icon: 'ri-user-add-line',
      color: 'orange',
    },
    {
      title: '付费用户数',
      value: '8,234',
      change: '付费率: 56.1%',
      trend: 'neutral',
      icon: 'ri-vip-crown-line',
      color: 'amber',
    },
    {
      title: '客单价',
      value: '¥298.5',
      change: '+3.2%',
      trend: 'up',
      icon: 'ri-price-tag-line',
      color: 'rose',
    },
    {
      title: '用户留存率',
      value: '78.5%',
      change: '7日: 65.2% | 30日: 52.8%',
      trend: 'neutral',
      icon: 'ri-line-chart-line',
      color: 'indigo',
    },
  ];

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; icon: string }> = {
      teal: { bg: 'bg-teal-50', text: 'text-teal-600', icon: 'bg-teal-100' },
      blue: { bg: 'bg-blue-50', text: 'text-blue-600', icon: 'bg-blue-100' },
      purple: { bg: 'bg-purple-50', text: 'text-purple-600', icon: 'bg-purple-100' },
      green: { bg: 'bg-green-50', text: 'text-green-600', icon: 'bg-green-100' },
      orange: { bg: 'bg-orange-50', text: 'text-orange-600', icon: 'bg-orange-100' },
      amber: { bg: 'bg-amber-50', text: 'text-amber-600', icon: 'bg-amber-100' },
      rose: { bg: 'bg-rose-50', text: 'text-rose-600', icon: 'bg-rose-100' },
      indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', icon: 'bg-indigo-100' },
    };
    return colors[color] || colors.teal;
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">数据概览</h1>
            <p className="text-sm text-gray-500 mt-1">实时监控核心业务指标与趋势</p>
          </div>
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1">
            {(['today', 'yesterday', 'week', 'month'] as TimePeriod[]).map((period) => (
              <button
                key={period}
                onClick={() => setTimePeriod(period)}
                className={`px-4 py-1.5 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                  timePeriod === period
                    ? 'bg-teal-500 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {period === 'today' && '今日'}
                {period === 'yesterday' && '昨日'}
                {period === 'week' && '本周'}
                {period === 'month' && '本月'}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-5">
          {metrics.map((metric, index) => {
            const colors = getColorClasses(metric.color);
            return (
              <div key={index} className="bg-white rounded-lg p-5 border border-gray-200">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-11 h-11 ${colors.icon} rounded-lg flex items-center justify-center`}>
                    <i className={`${metric.icon} text-xl ${colors.text}`}></i>
                  </div>
                  {metric.trend === 'up' && (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-arrow-up-line"></i>
                      {metric.change}
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500 mb-1">{metric.title}</div>
                <div className="text-2xl font-semibold text-gray-900 mb-2">{metric.value}</div>
                {metric.trend === 'neutral' && (
                  <div className="text-xs text-gray-500">{metric.change}</div>
                )}
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">用户增长趋势</h2>
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-1">
              {(['7days', '30days', '90days', 'custom'] as TrendPeriod[]).map((period) => (
                <button
                  key={period}
                  onClick={() => setTrendPeriod(period)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                    trendPeriod === period
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {period === '7days' && '7 天'}
                  {period === '30days' && '30 天'}
                  {period === '90days' && '90 天'}
                  {period === 'custom' && '自定义'}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={userGrowthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '12px' }} />
              <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line
                type="monotone"
                dataKey="new"
                stroke="#14b8a6"
                strokeWidth={2}
                name="新增用户"
                dot={{ fill: '#14b8a6', r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="total"
                stroke="#3b82f6"
                strokeWidth={2}
                name="累计用户"
                dot={{ fill: '#3b82f6', r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="active"
                stroke="#f59e0b"
                strokeWidth={2}
                name="活跃用户"
                dot={{ fill: '#f59e0b', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-2 gap-5">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">收入趋势</h2>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line
                  type="monotone"
                  dataKey="consumption"
                  stroke="#ef4444"
                  strokeWidth={2}
                  name="每日消费"
                  dot={{ fill: '#ef4444', r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="recharge"
                  stroke="#10b981"
                  strokeWidth={2}
                  name="每日充值"
                  dot={{ fill: '#10b981', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">实例使用情况</h2>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={instanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="created" fill="#14b8a6" name="创建数" radius={[4, 4, 0, 0]} />
                <Bar dataKey="deleted" fill="#f59e0b" name="删除数" radius={[4, 4, 0, 0]} />
                <Bar dataKey="net" fill="#8b5cf6" name="净增数" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
